import { zodResolver } from '@hookform/resolvers/zod';
import { Button, ButtonSize, ButtonVariant } from 'gcs-common/design-system/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'gcs-common/design-system/ui/form';
import StarRating from 'gcs-common/design-system/ui/star-rating';
import { Textarea } from 'gcs-common/design-system/ui/text-area';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

interface TicketRatingModalProps {
  comment: string;
  rating: number;
  handleRating: (rating: number, comment: string) => Promise<void>;
  handleCancel: () => void;
  handleDelete: () => Promise<void>;
  open: boolean;
  isAddLoading: boolean;
  isDeleteLoading: boolean;
}

const FormSchema = z.object({
  comment: z.string(),
  rating: z.number().min(1, 'Bitte geben Sie eine Bewertung ab'),
});

const TicketRatingModal = ({
  comment,
  rating,
  handleRating,
  handleCancel,
  handleDelete,
  isAddLoading,
  isDeleteLoading,
}: TicketRatingModalProps) => {
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      comment: comment ?? '',
      rating: rating ?? 0,
    },
  });

  const ratingValue = useWatch({ control: form.control, name: 'rating' });

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    await handleRating(data.rating, data.comment);
    form.reset(data); // Reset form to submitted values
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="rating"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <StarRating
                  ratingValue={field.value}
                  setRatingValue={(newRating) => form.setValue('rating', newRating, { shouldDirty: true })}
                  isEditable
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  {...field}
                  autoFocus
                  onClick={(e) => e.currentTarget.focus()}
                  className="relative box-border h-[120px] w-full rounded-md border border-neutral-alpha-5 border-solid bg-white p-3 text-base text-neutral-12"
                  placeholder="Kommentar (optional)"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex w-full flex-col gap-y-4 py-4">
          <Button
            type="submit"
            variant={ButtonVariant.solid_accent}
            isLoading={isAddLoading}
            size={ButtonSize.xl}
            disabled={!form.formState.isDirty || !ratingValue}
          >
            Bewerten
          </Button>

          {rating > 0 && (
            <Button
              type="button"
              variant={ButtonVariant.solid_error}
              size={ButtonSize.xl}
              isLoading={isDeleteLoading}
              onClick={async () => {
                await handleDelete();
                form.reset({ rating: 0, comment: '' });
              }}
            >
              Bewertung löschen
            </Button>
          )}

          <Button
            type="button"
            size={ButtonSize.xl}
            variant={ButtonVariant.soft_neutral}
            onClick={() => {
              handleCancel();
              form.reset(); // Reset form on cancel
            }}
          >
            Abbrechen
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default TicketRatingModal;
